import React, { useEffect, useRef, useState } from 'react'
import { Button, Col, Form, Modal, Row, Spinner, Table } from 'react-bootstrap'
import Select from 'react-select'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import { Info } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import {
  notifyError,
  notifySuccess,
} from '@common/utils/notify'
import { describeApiError } from '@common/utils/apiErrors'
import { useAuthStore } from '@store/auth.store'
import { validatePhoneNumberStructure } from '@common/utils/helperFunctions'
import Account from '@data/accounts/model/account'
import CustomerLocation from '@data/locations/model/customerLocation'
import {
  useAccountLocationsQuery,
} from '@data/locations/accountLocations'
import { useGroupsQuery } from '@data/rbac/rbac'
import {
  useAddUserLocationMutation,
  useAddUserToGroupsMutation,
  useRemoveUserFromGroupsMutation,
  useRemoveUserLocationMutation,
  useUpdateUserAccountMutation,
  useUpdateUserLocationMutation,
  useUpdateUserMutation,
  ACCOUNT_USERS_QUERY_KEY,
  USER_QUERY_KEY,
  USERS_QUERY_KEY,
  useUserQuery,
} from '@data/users/users'
import {
  InviteLocation,
  UserLocation,
} from '@data/users/model/userManagement'
import User, {
  ClientUserRole,
  Group,
  UserAccountAssoc,
} from '@data/users/model/user'

import {
  popoverAccountActive,
  popoverAccountAdmin,
  popoverAutoAddAlerts,
  popoverAutoAddLocations,
  popoverLocationAdmin,
  popoverAccountContact,
  popoverAccountInvoices,
} from './Popovers'

import {
  summariseSave,
  UserSavePart,
} from './userSaveOutcome'

import './Modal.scss'
import { ClientPositionOptions } from '../constants'
import { hasPermissions } from '@common/utils/validation/authValidators'

// Stable identities for the "no data yet" case. A `= []` default in a destructure allocates a
// new array on every render, and both of these feed the dependency array of the effect that
// seeds the form — so the effect re-ran on every render, re-set state with fresh arrays, and
// rendered again, without end.
const NO_GROUPS: Group[] = []
const NO_LOCATIONS: CustomerLocation[] = []

export interface IModalProps {
  show: boolean
  user: User
  account?: Account
  onHide: () => void
}

const EditModal: React.FC<IModalProps> = ({
  show,
  account,
  user,
  onHide,
}: IModalProps) => {
  const { data: groups = NO_GROUPS } = useGroupsQuery()
  const { data: accountLocations = NO_LOCATIONS } = useAccountLocationsQuery(
    account?.id ?? null,
    show,
  )
  const { data: selectedUser, isFetching: isFetchingUser } = useUserQuery(user?.id ?? null, show)
  // mutateAsync throughout: the save waits for every request and reports once (see handleSave).
  // These hooks have no onError of their own for the same reason.
  const { mutateAsync: updateUser } = useUpdateUserMutation()
  const { mutateAsync: addUserToGroups } = useAddUserToGroupsMutation()
  const { mutateAsync: removeUserFromGroups } = useRemoveUserFromGroupsMutation()
  const { mutateAsync: removeUserLocation } = useRemoveUserLocationMutation()
  const { mutateAsync: updateUserLocation } = useUpdateUserLocationMutation()
  const { mutateAsync: addUserLocation } = useAddUserLocationMutation()
  const { mutateAsync: updateUserAccount } = useUpdateUserAccountMutation()
  const queryClient = useQueryClient()
  const permissions = useAuthStore((state) => state.permissions)

  const [firstname, setFirstname] = useState('')
  const [lastname, setLastname] = useState('')
  const [mobile, setMobile] = useState('')
  const [email, setEmail] = useState('')
  // Defaults to 'Other', matching the invite modals. The previous '' is not a
  // ClientUserRoles value, so saving a user with no account association would have
  // been rejected with 422.
  const [position, setPosition] = useState<ClientUserRole>('Other')
  // const [selectedAccounts, setSelectedAccounts] = useState<Account[]>([])
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([])
  const [selectedLocations, setSelectedLocations] = useState<UserLocation[]>([])
  const [isAccountAdmin, setIsAccountAdmin] = useState(false)
  const [isAccountContact, setIsAccountContact] = useState(false)
  const [autoAddLocations, setAutoAddLocations] = useState(false)
  const [autoAddAlerts, setAutoAddAlerts] = useState(false)
  const [accountActive, setAccountActive] = useState(false)
  const [accountInvoice, setAccountInvoice] = useState(false)
  const [dataChange, setDataChange] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  const accountUpdate = useRef(false)
  const userUpdate = useRef(false)
  const locationUpdate = useRef(false)
  const permissionsUpdate = useRef(false)

  // Set after a save that partly failed. The user query is then refetched so a retry diffs
  // against what the server now holds, and this stops that refetch from re-seeding the form
  // over the edits the user is about to retry. Cleared whenever the modal opens.
  const keepEdits = useRef(false)

  useEffect(() => {
    keepEdits.current = false
  }, [show, user?.id])

  useEffect(() => {
    if (keepEdits.current) return

    setFirstname(selectedUser?.firstname || '')
    setLastname(selectedUser?.lastname || '')
    setMobile(selectedUser?.mobile || '')
    setEmail(selectedUser?.email || '')
    setAccountActive(selectedUser?.active || false)

    if (groups && selectedUser?.groups) {
      setSelectedGroups(mapGroups(selectedUser.groups))
    } else {
      setSelectedGroups([])
    }

    if (
      account &&
      accountLocations.length > 0 &&
      selectedUser?.customerLocations
    ) {
      setSelectedLocations(mapLocations(selectedUser.customerLocations))
    } else {
      setSelectedLocations([])
    }

    // if (accounts && selectedUser && selectedUser.accountIds) {
    //   setSelectedAccounts(mapAccounts(selectedUser.accountIds))
    // } else {
    //   setSelectedAccounts([])
    // }

    if (account && selectedUser?.adminAccountIds) {
      setIsAccountAdmin(!!selectedUser.accounts.find((a) => a.accountId === account.id && a.admin))
    }

    if (account && selectedUser?.accountContactIds) {
      setIsAccountContact(!!selectedUser.accounts.find((a) => a.accountId === account.id && a.accountContact))
    }

    if (account && selectedUser?.viewAllLocationsAccountIds) {
      setAutoAddLocations(!!selectedUser.viewAllLocationsAccountIds.find((i) => i === account.id))
    }

    if (account && selectedUser?.accounts) {
      setAutoAddAlerts(
        !!selectedUser.accounts.find((a) => a.accountId === account.id && a.autoAddAlerts),
      )
      setAccountInvoice(
        !!selectedUser.accounts.find((a) => a.accountId === account.id && a.invoice),
      )
      const accountAssociation = selectedUser.accounts.find((a) => a.accountId === account.id)
      setPosition(
        accountAssociation ? accountAssociation.position : 'Other'
      )
    }
  }, [selectedUser, groups, accountLocations, account])

  // useEffect(() => {
  //   if (!selectedUser || !account) return
  //   let viewAll = selectedUser.viewAllLocationsAccountIds
  //   if (!viewAll.includes(account.id)) {
  //     viewAll.push(account.id)
  //   } else {
  //     viewAll = viewAll.filter((accId) => accId !== account.id)
  //   }
  //   setSelectedUser({
  //     ...selectedUser,
  //     viewAllLocationsAccountIds: viewAll,
  //   })
  // }, [viewAllLocations])

  // const mapAccounts = (selectedAccounts: string[]): Account[] => {
  //   return selectedAccounts
  //     .map((g) => accounts.find((ggg) => ggg.id === g))
  //     .filter((g) => !!g)
  //     .map((g) => g!)
  // }

  const mapLocations = (selectedLocations: UserLocation[]): UserLocation[] => {
    return selectedLocations.filter((loc) => {
      if (accountLocations.find((accLoc) => accLoc.id === loc.locationId)) return loc
    })
  }

  const mapGroups = (selectedGroups: string[]): Group[] => {
    return selectedGroups
      .map((g) => groups.find((ggg) => ggg.name === g))
      .filter((g) => !!g)
      .map((g) => g!)
  }

  const handleSave = async () => {
    const updatedUser: any = {
      id: user.id,
      active: accountActive,
      firstname: firstname.trim(),
      lastname: lastname.trim(),
      mobile: mobile.trim(),
      email: email.trim()
    }

    if (selectedUser && updatedUser.id) {
      const isMobileNumberValid = validatePhoneNumberStructure(mobile.trim())

      if (selectedLocations.some((loc) => loc.emergencyContact) && !isMobileNumberValid) {
        // displayToast closed errors after 5000 ms; kept, since notifyError defaults to sticky.
        notifyError(
          'Please add a valid mobile number before assigning this user as an emergency contact',
          {
            id: 'toastAvoidsDuplicates',
            autoClose: 5000,
          },
        )
      } else {
        // Every request starts here, in parallel, each labelled with the part of the form it
        // saves so the outcome can name what failed.
        const requests: { part: UserSavePart; promise: Promise<unknown> }[] = [
          ...(userUpdate.current ? [updateUser(updatedUser)] : [])
            .map((promise) => ({ part: 'details' as const, promise })),
          ...updateAccounts().map((promise) => ({ part: 'account settings' as const, promise })),
          ...updateGroups().map((promise) => ({ part: 'groups' as const, promise })),
          ...updateLocations().map((promise) => ({ part: 'locations' as const, promise })),
        ]

        if (!requests.length) {
          onHide()
          return
        }

        setIsSaving(true)

        const results = await Promise.allSettled(requests.map(({ promise }) => promise))
        const outcome = summariseSave(
          requests.map(({ part }, index) => ({
            part,
            result: results[index],
          })),
          describeApiError,
        )

        if (outcome.kind === 'error') {
          // A part whose requests all succeeded is done; clear its flag so a retry does not
          // resend it. A failed part keeps its flag, and the refetch below gives the retry a
          // fresh baseline to diff against.
          const partFlags: Record<UserSavePart, React.MutableRefObject<boolean>> = {
            'details': userUpdate,
            'account settings': accountUpdate,
            'groups': permissionsUpdate,
            'locations': locationUpdate,
          }
          for (const { part } of requests) {
            if (!outcome.failedParts.includes(part)) partFlags[part].current = false
          }

          keepEdits.current = true
        }

        // Once, after everything settled: parallel responses resolve in any order, so the
        // last one written into the users list may predate another request's change. The
        // hooks do not refetch on their own; when each did, the parallel requests cancelled
        // one another's account-users refetch, which showed as an aborted request.
        const refreshed = Promise.all([
          queryClient.invalidateQueries({ queryKey: USERS_QUERY_KEY }),
          queryClient.invalidateQueries({ queryKey: ACCOUNT_USERS_QUERY_KEY }),
          queryClient.invalidateQueries({ queryKey: [...USER_QUERY_KEY, user.id] }),
        ])

        if (outcome.kind === 'success') {
          // Not awaited: the body swaps to a spinner while the user query refetches, which
          // would flash before the modal closed.
          setIsSaving(false)
          notifySuccess('User updated successfully')
          onHide()
          return
        }

        // Awaited, so a retry diffs against what the server now holds.
        await refreshed
        setIsSaving(false)
        notifyError(outcome.message)
      }
    }
  }

  useEffect(() => {
    if (selectedUser) {
      if (selectedUser.firstname !== firstname) {
        userUpdate.current = true
        setDataChange(true)
      }
      if (selectedUser.lastname !== lastname) {
        userUpdate.current = true
        setDataChange(true)
      }
      if (selectedUser.mobile !== mobile) {
        userUpdate.current = true
        setDataChange(true)
      }
      if (selectedUser.active !== accountActive) {
        userUpdate.current = true
        setDataChange(true)
      }
    } else {
      userUpdate.current = false
      setDataChange(false)
    }
  }, [firstname, lastname, mobile, accountActive])



  useEffect(() => {
    if (selectedUser) {
      if (!selectedGroups.length) return
        const userGroupsSet = new Set(selectedUser.groups)
        const selectedGroupsSet = new Set(selectedGroups.map((group) => group.name));
        const groupsUpdated = !(userGroupsSet.size === selectedGroupsSet.size &&
          selectedUser.groups.every(item => selectedGroupsSet.has(item)))
        if (groupsUpdated) {
          permissionsUpdate.current = true
          setDataChange(true)
        }
      } else {
        permissionsUpdate.current = false
        setDataChange(false)
      }
  }, [selectedGroups])

  useEffect(() => {
    if (selectedUser && account) {
      if (
        !!selectedUser.accounts.find(
          (a) => a.accountId === account.id && a.admin !== isAccountAdmin,
        )
      ) {
        accountUpdate.current = true
        setDataChange(true)
      } else if (
        !!selectedUser.accounts.find(
          (a) => a.accountId === account.id && a.autoAddLocations !== autoAddLocations,
        )
      ) {
        accountUpdate.current = true
        setDataChange(true)
      } else if (
        !!selectedUser.accounts.find(
          (a) => a.accountId === account.id && a.autoAddAlerts !== autoAddAlerts,
        )
      ) {
        accountUpdate.current = true
        setDataChange(true)
      } else if (
        !!selectedUser.accounts.find(
          (a) => a.accountId === account.id && a.invoice !== accountInvoice,
        )
      ) {
        accountUpdate.current = true
        setDataChange(true)
      } else if (
        !!selectedUser.accounts.find(
          (a) => a.accountId === account.id && a.accountContact !== isAccountContact,
        )
      ) {
        accountUpdate.current = true
        setDataChange(true)
      } else if (
        !!selectedUser.accounts.find(
        (a) => a.accountId === account.id && a.position !== position,
      )) {
        accountUpdate.current = true
        setDataChange(true)
      } else
      {
        accountUpdate.current = false
        setDataChange(false)
      }
    }
  }, [isAccountAdmin, isAccountContact, autoAddLocations, autoAddAlerts, accountInvoice, position])

  const getDifference = (array1: UserLocation[], array2: UserLocation[]) => {
    return array1.filter(
      (object1: UserLocation) =>
        !array2.some(
          (object2: UserLocation) =>
            object1.locationId === object2.locationId &&
            object1.admin === object2.admin &&
            object1.emergencyContact === object2.emergencyContact,
        ),
    )
  }

  useEffect(() => {
    if (selectedUser) {
      const diff = [
        ...getDifference(selectedLocations, selectedUser.customerLocations),
        ...getDifference(selectedUser.customerLocations, selectedLocations),
      ].length

      if (
        diff !== 0 ||
        (account &&
          !!selectedUser.accounts.find(
            (a) => a.accountId === account.id && a.admin !== isAccountAdmin,
          ))
      ) {
        locationUpdate.current = true
        setDataChange(true)
      } else {
        locationUpdate.current = false
        setDataChange(false)
      }
    }
  }, [selectedLocations])

  const updateAccounts = (): Promise<unknown>[] => {
    const accountOptions: UserAccountAssoc[] = [
      {
        accountId: account!.id,
        admin: isAccountAdmin,
        autoAddAlerts: autoAddAlerts,
        autoAddLocations: autoAddLocations,
        invoice: accountInvoice,
        accountContact: isAccountContact,
        position: position
      },
    ]

    if (selectedUser && accountUpdate.current) {
      return [updateUserAccount({
        userId: selectedUser.id,
        accounts: accountOptions,
      })]
    }

    return []
  }

  const updateGroups = (): Promise<unknown>[] => {
    if (!permissionsUpdate.current) return []

    const requests: Promise<unknown>[] = []

    const removedGroups = mapGroups(selectedUser!.groups)
      .filter((g) => selectedGroups.findIndex((gg) => gg.id === g.id) === -1)
      .map((g) => g.id)

    const addedGroups = selectedGroups
      .filter((g) => !selectedUser?.groups.includes(g.name))
      .map((g) => g.id)

    if (addedGroups.length) {
      requests.push(addUserToGroups({
        user: selectedUser!,
        groupIds: addedGroups,
      }))
    }
    if (removedGroups.length) {
      requests.push(removeUserFromGroups({
        user: selectedUser!,
        groupIds: removedGroups,
      }))
    }

    return requests
  }

  const updateLocations = (): Promise<unknown>[] => {
    if (!locationUpdate.current) return []

    const requests: Promise<unknown>[] = []

    if (selectedUser && selectedUser.customerLocations) {
      const allowedLocations: UserLocation[] = selectedUser!.customerLocations.filter(
        (loc) => accountLocations.findIndex((accLoc) => accLoc.id === loc.locationId) !== -1,
      )
      const removedLocations = allowedLocations.filter(
        (loc2) => selectedLocations.findIndex((l) => l.locationId === loc2.locationId) === -1,
      )

      if (removedLocations.length) {
        requests.push(removeUserLocation({
          userId: selectedUser.id,
          locations: removedLocations,
        }))
      }

      const addedLocations = selectedLocations.filter(
        (loc) => !selectedUser?.customerLocations.find((l) => l.locationId === loc.locationId),
      )
      if (addedLocations.length) {
        requests.push(addUserLocation({
          userId: selectedUser.id,
          locations: addedLocations,
        }))
      }

      const updatedLocations = selectedLocations.filter((loc) =>
        selectedUser.customerLocations.find(
          (l) =>
            l.locationId === loc.locationId &&
            (l.admin !== loc.admin || l.emergencyContact !== loc.emergencyContact),
        ),
      )

      if (updatedLocations.length) {
        requests.push(updateUserLocation({
          userId: selectedUser.id,
          locations: updatedLocations,
        }))
      }
    }

    return requests
  }

  const handleAccountAdmin = (val: boolean) => {
    setIsAccountAdmin(val)
    if (!val) {
      setSelectedLocations([])
      // setSelectAllLocations(false);
    } else {
      const locations: InviteLocation[] = accountLocations.map((l: CustomerLocation) => {
        return { locationId: l.id, admin: true, alerts: false } as InviteLocation
      })
      setSelectedLocations(locations)
      // setSelectAllLocations(true);
    }
  }

  const handleLocations = (location: string, admin: boolean) => {
    // if (location === 'allLoc') {
    //   if (!selectAllLocations) {
    //     const locations: InviteLocation[] = accountLocations.map((l: CustomerLocation) => {
    //       return {locationId: l.id, admin: false, alerts: false} as InviteLocation
    //     })
    //     setSelectedLocations(locations);
    //   } else {
    //     setSelectedLocations([]);
    //   }
    //   setSelectAllLocations(!selectAllLocations);
    //   return;
    // } else {
    //   setSelectAllLocations(false);
    // }
    //
    const locationIndex = selectedLocations.findIndex((x) => x.locationId === location)
    if (locationIndex !== -1) {
      const locations = [...selectedLocations]
      if (admin) {
        locations[locationIndex] = {
          ...locations[locationIndex],
          admin: !locations[locationIndex].admin,
          emergencyContact: locations[locationIndex].emergencyContact,
        }
      } else {
        locations.splice(locationIndex, 1)
      }
      setSelectedLocations(locations)
    } else {
      setSelectedLocations([
        ...selectedLocations,
        { locationId: location, admin: false, emergencyContact: false },
      ])
    }
  }

  const handleEmergencyContact = (location: string) => {
    const locationIndex = selectedLocations.findIndex((x) => x.locationId === location)

    if (locationIndex !== -1) {
      const locations = [...selectedLocations]
      const locationCurrentValue = locations[locationIndex].emergencyContact
      locations[locationIndex] = {
        ...locations[locationIndex],
        emergencyContact: !locationCurrentValue,
      }

      setSelectedLocations(locations)
    } else {
      setSelectedLocations([
        ...selectedLocations,
        { locationId: location, admin: false, emergencyContact: false },
      ])
    }
  }
  // noinspection RequiredAttributes
  return (
    <Modal
      show={show}
      // The header close button and Escape both land here; neither may close mid-save.
      onHide={() => {
        if (!isSaving) onHide()
      }}
      backdrop='static'
      size='xl'
      aria-labelledby='contained-modal-title-vcenter'
      centered
    >
      <Modal.Header className='modal-header' closeButton>
        <Modal.Title id='contained-modal-title-vcenter'>{`${firstname} ${lastname} - ${user.email}`}</Modal.Title>
      </Modal.Header>
      {!isFetchingUser ? (
        <>
          <Modal.Body>
            <Form className='user-information-form'>
              <Row className='mb-4 fw-bold'>
                <Form.Group as={Col} controlId='formGridEmail'>
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type='text'
                    disabled={!!selectedUser?.id}
                    placeholder='Enter a Email'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </Form.Group>
                <Form.Group as={Col} controlId='formGridUserId'>
                  <Form.Label>ID</Form.Label>
                  <Form.Control
                    type='text'
                    value={user.id}
                    disabled
                  />
                </Form.Group>
              </Row>
              <Row className='mb-4 fw-bold'>
                <Form.Group as={Col} controlId='formGridFirstName'>
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type='text'
                    placeholder='Enter a Firstname'
                    value={firstname}
                    onChange={(e) => setFirstname(e.target.value)}
                  />
                </Form.Group>
                <Form.Group as={Col} controlId='formGridLastName'>
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type='text'
                    placeholder='Enter a Lastname'
                    value={lastname}
                    onChange={(e) => setLastname(e.target.value)}
                  />
                </Form.Group>
              </Row>
              <Row className='mb-4 fw-bold'>
                <Form.Group as={Col} controlId='formGridMobile'>
                  <Form.Label>Mobile</Form.Label>
                  <Form.Control
                    type='text'
                    placeholder='Enter a Mobile'
                    value={mobile}
                    onChange={(e) => setMobile(e.target.value.trim())}
                  />
                </Form.Group>
                <Form.Group as={Col} controlId='formGridPosition'>
                  <Form.Label>Position</Form.Label>
                  <Select
                    classNamePrefix='select'
                    value={{
                      value: position,
                      label: position
                    }}
                    onChange={(e: any) => setPosition(e.value)}
                    options={
                      ClientPositionOptions.map((opt) => {
                        return { value: opt,  label: opt }
                      })
                    }
                    getOptionValue={(option) => option.value}
                    getOptionLabel={(option) => `${option.label}`}
                    isClearable={false}
                  />
                </Form.Group>
              </Row>
              <Row className='mb-4 fw-bold'>
                <Form.Group as={Col} controlId='accountOptions'>
                  <Form.Label>Account Settings</Form.Label>
                  <div>
                    <Form.Switch
                      type='checkbox'
                      inline
                      onChange={() => setAccountActive(!accountActive)}
                      checked={accountActive}
                      label={
                        <>
                          Enabled
                          <OverlayTrigger
                            delay={{ hide: 450, show: 300 }}
                            trigger={['hover', 'focus']}
                            overlay={popoverAccountActive}
                            placement='bottom'
                          >
                            <Info size={15} className={'ml-1 text-info'} />
                          </OverlayTrigger>
                        </>
                      }
                      name='accountOptions'
                      id={`inline-account-options-6`}
                    />
                    <Form.Switch
                      type='checkbox'
                      inline
                      onChange={() => handleAccountAdmin(!isAccountAdmin)}
                      checked={isAccountAdmin}
                      label={
                        <>
                          Account Admin
                          <OverlayTrigger
                            delay={{ hide: 450, show: 300 }}
                            trigger={['hover', 'focus']}
                            overlay={popoverAccountAdmin}
                            placement='bottom'
                          >
                            <Info size={15} className={'ml-1 text-info'} />
                          </OverlayTrigger>
                        </>
                      }
                      name='accountOptions'
                      id={`inline-account-options-1`}
                    />
                    <Form.Switch
                      type='checkbox'
                      inline
                      onChange={() => setIsAccountContact(!isAccountContact)}
                      checked={isAccountContact}
                      label={
                        <>
                          Account Contact
                          <OverlayTrigger
                            delay={{ hide: 450, show: 300 }}
                            trigger={['hover', 'focus']}
                            overlay={popoverAccountContact}
                            placement='bottom'
                          >
                            <Info size={15} className={'ml-1 text-info'} />
                          </OverlayTrigger>
                        </>
                      }
                      name='accountOptions'
                      id={`inline-account-options-2`}
                    />
                    <Form.Switch
                      type='checkbox'
                      inline
                      onChange={() => setAccountInvoice(!accountInvoice)}
                      checked={accountInvoice}
                      label={
                        <>
                          Account Invoices
                          <OverlayTrigger
                            delay={{ hide: 450, show: 300 }}
                            trigger={['hover', 'focus']}
                            overlay={popoverAccountInvoices}
                            placement='bottom'
                          >
                            <Info size={15} className={'ml-1 text-info'} />
                          </OverlayTrigger>
                        </>
                      }
                      name='accountOptions'
                      id={`inline-account-options-3`}
                    />
                  </div>
                </Form.Group>
              </Row>
              <Row className='mb-4 fw-bold'>
                <Form.Group as={Col} controlId='accountOptions'>
                  <Form.Label>Account Location Settings</Form.Label>
                  <div>
                    <Form.Switch
                      type='checkbox'
                      inline
                      onChange={() => setAutoAddLocations(!autoAddLocations)}
                      checked={autoAddLocations || isAccountAdmin}
                      disabled={isAccountAdmin}
                      label={
                        <>
                          Auto Add New Locations
                          <OverlayTrigger
                            delay={{ hide: 450, show: 300 }}
                            trigger={['hover', 'focus']}
                            overlay={popoverAutoAddLocations}
                            placement='bottom'
                          >
                            <Info size={15} className={'ml-1 text-info'} />
                          </OverlayTrigger>
                        </>
                      }
                      name='accountOptions'
                      id={`inline-account-options-4`}
                    />
                    <Form.Switch
                      type='checkbox'
                      inline
                      onChange={() => setAutoAddAlerts(!autoAddAlerts)}
                      checked={autoAddAlerts}
                      label={
                        <>
                          Auto Add Alerts
                          <OverlayTrigger
                            delay={{ hide: 450, show: 300 }}
                            trigger={['hover', 'focus']}
                            overlay={popoverAutoAddAlerts}
                            placement='bottom'
                          >
                            <Info size={15} className={'ml-1 text-info'} />
                          </OverlayTrigger>
                        </>
                      }
                      name='accountOptions'
                      id={`inline-account-options-5`}
                    />
                  </div>
                </Form.Group>
              </Row>

              {hasPermissions(permissions, ['CREATE:USER', 'UPDATE:GROUPS']) && (
                <Row className='mb-4 fw-bold'>
                  <Form.Group as={Col} controlId='formGridName'>
                    <Form.Label>Groups</Form.Label>
                    <Select
                      className='basic-multi-select'
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 2 }) }}
                      menuPosition={'fixed'}
                      isDisabled={!selectedUser}
                      value={selectedGroups}
                      onChange={(options: any) => setSelectedGroups([...options])}
                      options={groups}
                      isMulti
                      getOptionValue={(option) => option.name}
                      getOptionLabel={(option) => option.description}
                      isClearable={false}
                    />
                  </Form.Group>
                </Row>
              )}

              <Row>
                <Form.Group as={Col} controlId='formGridLocations'>
                  <Form.Label>Locations</Form.Label>
                  <div style={{ width: '100%', height: '300px', overflowY: 'auto' }}>
                    <Table striped>
                      <thead
                        style={{
                          width: '15%',
                          position: 'sticky',
                          top: -1,
                          zIndex: 1,
                        }}
                      >
                        <tr>
                          <th>Name</th>
                          <th className={'text-center'}>
                            Admin
                            <OverlayTrigger
                              delay={{ hide: 450, show: 300 }}
                              trigger={['hover', 'focus']}
                              overlay={popoverLocationAdmin}
                              // containerPadding={20}
                              placement='bottom'
                            >
                              <Info size={15} className={'ml-1 text-info'} />
                            </OverlayTrigger>
                          </th>
                          <th className={'text-center'}>Emergency Contact</th>
                        </tr>
                      </thead>
                      <tbody>
                        {accountLocations.map((l) => (
                          <tr key={l.id}>
                            <td>
                              <Form.Check
                                type={'checkbox'}
                                label={l.name}
                                value={l.id}
                                id={`default-${l.id}`}
                                onChange={(e) => handleLocations(e.target.value, false)}
                                className={'ml-2'}
                                checked={!!selectedLocations.find((loc) => loc.locationId === l.id)}
                                disabled={isAccountAdmin}
                              />
                            </td>
                            <td className={'text-center'}>
                              <Form.Check
                                type='checkbox'
                                value={l.id}
                                label={''}
                                disabled={
                                  !selectedLocations.find((loc) => loc.locationId === l.id) ||
                                  isAccountAdmin
                                }
                                onChange={(e) => handleLocations(e.target.value, true)}
                                checked={
                                  !!selectedLocations.find(
                                    (i) => i.locationId === l.id && i.admin,
                                  ) || isAccountAdmin
                                }
                              />
                            </td>
                            <td className={'text-center'}>
                              <Form.Check
                                type='checkbox'
                                value={l.id}
                                label={''}
                                disabled={!selectedLocations.find((loc) => loc.locationId === l.id)}
                                onChange={(e) => handleEmergencyContact(e.target.value)}
                                checked={
                                  !!selectedLocations.find(
                                    (i) => i.locationId === l.id && i.emergencyContact,
                                  )
                                }
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Form.Group>
              </Row>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button
              className='btn btn-secondary'
              onClick={onHide}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={!dataChange || isSaving}>
              {isSaving ? (
                <Spinner
                  animation='border'
                  size='sm'
                  role='status'
                  aria-label='Saving'
                />
              ) : 'Save Changes & Close'}
            </Button>
          </Modal.Footer>
        </>
      ) : (
        <Spinner
          as='span'
          variant='primary'
          animation='border'
          role='status'
          aria-hidden='true'
          className='align-self-center mt-5 mb-5'
        />
      )}
    </Modal>
  )
}

export default EditModal
